import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from "@tanstack/react-query";

import { baseUrl } from "../../../constants";
import { apiClient } from "../../api";
import {
  GET_VIRTUAL_ACCOUNTS_API_RESPONSE,
  GET_VIRTUAL_ACCOUNT_BANKS_API_RESPONSE,
  CreateVirtualAccountPayload,
  VirtualAccountBank,
} from "./types";
import keys from "./keys";

interface GetVirtualAccountsReturnType {
  isLoading: boolean;
  data: GET_VIRTUAL_ACCOUNTS_API_RESPONSE | null;
  isSuccess: boolean;
  refetch: () => void;
  error: unknown;
}

interface GetVirtualAccountBanksReturnType {
  isLoading: boolean;
  banks: VirtualAccountBank[];
  error: unknown;
}

export const virtualAccountsResourcePath = "business-virtual-accounts/me";
export const virtualAccountBanksResourcePath = "virtual-accounts/banks";

const useGetVirtualAccounts = (url: string): GetVirtualAccountsReturnType => {
  // Include the URL in the query key to ensure different URLs are cached separately
  const hash = [keys.read, url];

  const {
    isLoading,
    data = null,
    isSuccess,
    error,
    refetch,
  }: UseQueryResult<
    GET_VIRTUAL_ACCOUNTS_API_RESPONSE | null,
    unknown
  > = useQuery({
    queryKey: hash,
    queryFn: async () => await apiClient.get({ url }),
    enabled: !!url,
    refetchInterval: (data) => {
      // Check if there are any accounts with incomplete data (null accountName)
      const hasIncompleteAccounts = data?.data?.list?.some(
        (account) => !account.accountName || !account.accountNumber,
      );

      // Poll every 3 seconds if there are incomplete accounts, otherwise stop polling
      return hasIncompleteAccounts ? 3000 : false;
    },
  });

  return { isLoading, data, isSuccess, error, refetch };
};

const useCreateVirtualAccount = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ data }: { data?: CreateVirtualAccountPayload }) => {
      // Use test bank code in non-production environments
      const payload =
        process.env.NODE_ENV !== "production"
          ? { ...data, bankCode: "test-bank" }
          : data;

      const response = await apiClient.post({
        url: `${baseUrl}/${virtualAccountsResourcePath}`,
        body: payload,
      });

      return response.data;
    },
    onSuccess: () => {
      // Invalidate the virtual accounts query to refetch the latest data
      queryClient.invalidateQueries({
        queryKey: [keys.read],
        refetchType: "active",
      });
    },
  });
};

const useGetVirtualAccountBanks = (): GetVirtualAccountBanksReturnType => {
  const {
    isLoading,
    data = null,
    error,
  }: UseQueryResult<
    GET_VIRTUAL_ACCOUNT_BANKS_API_RESPONSE | null,
    unknown
  > = useQuery({
    // Distinct key (not keys.read) so creating an account doesn't invalidate
    // this cache. The bank list hardly ever changes, so fetch it once per
    // session and never auto-refetch. Both use Infinity rather than a literal
    // "6 months": staleTime and cacheTime are scheduled with setTimeout, which
    // overflows past ~24.8 days (32-bit limit) and clamps to 1ms — Infinity is
    // handled specially (no timer) and is what "cache long-term" really means
    // for an in-memory cache that resets on full reload anyway.
    queryKey: [virtualAccountBanksResourcePath],
    queryFn: async () =>
      await apiClient.get({ url: virtualAccountBanksResourcePath }),
    staleTime: Infinity,
    cacheTime: Infinity,
    refetchOnWindowFocus: false,
  });

  return { isLoading, banks: data?.data ?? [], error };
};

export {
  useGetVirtualAccounts,
  useGetVirtualAccountBanks,
  useCreateVirtualAccount,
};
